import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Avatar, Box, Button, Grid, IconButton, Stack, Typography } from '@mui/material'

import ButtonCustom from '../../components/atoms/button/ButtonCustom'
import CalendarSync from '../../components/atoms/CalendarSync/CalendarSync'
import ListToolbar from '../../components/dataGrid/ListToolBar'
import FilterPopover from '../../components/dataGrid/FilterPopover'
import DataGridTable from '../../components/dataGrid/DataGridTable'
import StatusBtn from '../../components/atoms/status/StatusBtn'
import KebabMenu from '../../components/dataGrid/KebabMenu'
import CommonModal from '../../components/atoms/modal/CommonModal'
import MultiselectSearch from '../../components/atoms/select/MultiSelect'
import DateCustom from '../../components/atoms/input/DateCustom'
import SelectCustom from '../../components/atoms/select/SelectCustom'
import TextArea from '../../components/atoms/textarea/TextArea'
import FileAttach from '../../components/atoms/file-attach/FileAttach'
import CommonDeleteModal from '../../components/atoms/modal/CommonDeleteModal'
import { VendorTimeline } from '../profile/VendorTimeline'
import SyncAllModal from './SyncAllModal'
import {
	createVendorTask,
	getVendorEvents,
	deleteVendorTask,
	updateVendorTask,
	getWeddingsEvents,
} from '../../features/schedulerSlice'
import { getStaffInfo } from '../../features/vendorSlice'
import { toastHelper } from '../../util/toastHelper'
import { TASK_STATUS_OPTIONS, EVENT_CATEGORIES } from '../../util/constants'
import { getDefaultSyncFromConnections } from '../../util/calendarProviders'
import { formatUtcToLocalDate } from '../../util/formatDateAndTime'

import styles from './TasksPage.module.scss'
import { getKazoos } from '../../features/kazooEventSlice'
import { useVendorOrPlannerSubscriptionAccess } from '../../hooks'

function esc(s) {
	const str = String(s ?? '')
	return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str
}

const availableStatuses = TASK_STATUS_OPTIONS.filter(
	(option) => option.value !== 'Completed'
).map((option) => option.value)

export default function TasksPage() {
	const subscriptionAccess = useVendorOrPlannerSubscriptionAccess()
	const authUser = useSelector((s) => s.auth?.user)

	const [search, setSearch] = useState('')
	const [statusFilter, setStatusFilter] = useState(availableStatuses)
	const [priorityFilter, setPriorityFilter] = useState([])
	const [filterAnchor, setFilterAnchor] = useState(null)
	const [menuAnchor, setMenuAnchor] = useState(null)
	const [menuRow, setMenuRow] = useState(null)
	const [selectedMembers, setSelectedMembers] = useState([])
	const [text, setText] = useState('')
	const [innerTab, setInnerTab] = useState(0)
	const [activeModal, setActiveModal] = useState('')
	const [selectedTaskId, setSelectedTaskId] = useState(null)
	const [taskTitle, setTaskTitle] = useState('')
	const [dueDate, setDueDate] = useState(null) // Date or 'YYYY-MM-DD' string
	const [priorityVal, setPriorityVal] = useState('high') // 'high' | 'medium' | 'low'
	const [statusVal, setStatusVal] = useState('new') // 'new' | 'inprogress' | 'completed' | 'overdue'
	const [eventCategory, setEventCategory] = useState({
		value: '',
		label: '',
	})
	const [selectedWedding, setSelectedWedding] = useState('none')
	const [syncToCalendar, setSyncToCalendar] = useState({
		google: true,
		outlook: true,
		ical: true,
	})
	const accountType = useSelector((s) => s.auth?.user?.__t)
	const isBusinessPro = useMemo(() => ['business-pro', 'business-pro-month'].includes(subscriptionAccess?.payment), [subscriptionAccess?.payment])
	const restrictedAccountTypes = ['vendor-staff', 'planner-staff']
	const connections = useSelector((state) => state.calendar.connections)

	const dispatch = useDispatch()
	const allEvents = useSelector((s) => s.scheduler.vendorTasks) || []
	const reduxWeddings = useSelector((s) => s.kazooEvent.kazoos || [])

	const weddings = useMemo(
		() => [
			...(reduxWeddings?.map((c) => ({
				value: c._id,
				label: c.kazooName,
			})) || []),
			{ value: 'none', label: 'None' },
		],
		[reduxWeddings]
	)

	const weddingById = useMemo(
		() =>
			reduxWeddings?.find((wedding) => wedding._id === selectedWedding),
		[reduxWeddings, selectedWedding]
	)

	useEffect(() => {
		if (activeModal === 'create') {
			setSyncToCalendar(getDefaultSyncFromConnections(connections))
		}
	}, [activeModal, connections])

	const eventCategoriesOptions = useMemo(() => 
		EVENT_CATEGORIES.map((category) => ({
			value: category,
			label: category,
		})),
		[EVENT_CATEGORIES]
	)

	const vendorEvents = useMemo(
		() => allEvents.filter((e) => e.VendorId),
		[allEvents]
	)

	const uniqueEvents = useMemo(() => {
		const m = new Map()
		for (const e of vendorEvents) if (!m.has(e._id)) m.set(e._id, e)
		return Array.from(m.values())
	}, [vendorEvents])

	const tasksOnly = useMemo(
		() => uniqueEvents,
		[uniqueEvents]
	)
	
	useEffect(() => {
		dispatch(getVendorEvents('isVendorTasks'))
		dispatch(getKazoos())
		dispatch(getStaffInfo())
	}, [dispatch])

	useEffect(() => {
		if (accountType === 'vendor') {
			dispatch(getKazoos())
		}
	}, [dispatch, accountType])

	const PRIORITY_LABEL = { high: 0, medium: 1, low: 2 }
	const STATUS_LABEL = {
		new: 'New',
		inprogress: 'In Progress',
		completed: 'Completed',
		overdue: 'Overdue',
	}
	const getVal = (v) => (typeof v === 'string' ? v : v?.value || '')
	const getNormalizedAssigneeIds = (members = []) =>
		(members || [])
			.map((m) => String(m?._id || m?.id || m || ''))
			.filter(Boolean)



	const staff = useSelector((s) => s.vendor?.staff)?.filter((s) => s.status === 'active') || []

	const staffForSelect = useMemo(() => {
		const seen = new Set()
		if(!isBusinessPro) return []
		return staff
			.map((s) => ({
				_id: String(s.staffId || s._id || ''),
				name: (s.name || s.email || 'Unknown').trim(),
				avatar: s.avatar,
				email: s.email,
			}))
			.filter((u) => u._id && !seen.has(u._id) && seen.add(u._id))
	}, [staff, isBusinessPro])

	const selfMember = useMemo(() => {
		const id = String(authUser?._id || '')
		if (!id) return null
		return {
			_id: id,
			name:
				(authUser?.businessName ||
					authUser?.name ||
					authUser?.email ||
					'Me'
				).trim(),
			avatar: authUser?.avatar || authUser?.profilePic || undefined,
			email: authUser?.email,
		}
	}, [authUser])

	const assignableMembers = useMemo(() => {
		const seen = new Set()
		return [selfMember, ...staffForSelect]
			.filter(Boolean)
			.filter((u) => u._id && !seen.has(u._id) && seen.add(u._id))
	}, [selfMember, staffForSelect])

	const availableMembers = useMemo(() => {
		const picked = new Set(
			(selectedMembers || []).map((m) => String(m._id))
		)
		return assignableMembers.filter((m) => !picked.has(String(m._id)))
	}, [assignableMembers, selectedMembers])

	const rows = useMemo(() => {
		const prMap = { 0: 'High', 1: 'Medium', 2: 'Low' }
		return tasksOnly.map((t) => ({
			id: t._id,
			task: t.Subject || 'Untitled Task',
			source:
				t.createdFrom === 'timeline-page'
					? 'Timeline'
					: t.createdFrom === 'tasks-page'
						? 'Task List'
						: 'Legacy',
			assignedTo:
				Array.isArray(t.OwnerIds) && t.OwnerIds.length
					? `${t.OwnerIds.length} assignee${t.OwnerIds.length > 1 ? 's' : ''}`
					: 'N/A',
			avatar: undefined,
			dueDate: (() => {
				const d = t.EndTime || t.StartTime
				if (!d) return '-'
				return formatUtcToLocalDate(d)
			})(),
			priority: prMap[Number(t.priority)] || 'Unknown',
			comments: t.Description || '-',
			status: t.status || 'New',
		}))
	}, [tasksOnly])

	const priority_options = [
		{
			value: 'high',
			label: (
				<div className={`${styles.prioritySelect} prioritySelect`}>
					<img src="/images/high-prio-icon.svg" alt="high" />
					High
				</div>
			),
		},
		{
			value: 'medium',
			label: (
				<div className={`${styles.prioritySelect} prioritySelect`}>
					<img src="/images/med-prio-icon.svg" alt="med" />
					Medium
				</div>
			),
		},
		{
			value: 'low',
			label: (
				<div className={`${styles.prioritySelect} prioritySelect`}>
					<img src="/images/low-prio-icon.svg" alt="low" />
					Low
				</div>
			),
		},
	]
	const status_options = [
		{
			value: 'new',
			label: (
				<StatusBtn
					label="New"
					variant="active"
					className={styles.statusOption}
				/>
			),
		},
		{
			value: 'inprogress',
			label: (
				<StatusBtn
					label="In Progress"
					variant="pending"
					className={styles.statusOption}
				/>
			),
		},
		{
			value: 'completed',
			label: (
				<StatusBtn
					label="Completed"
					variant="submitted"
					className={styles.statusOption}
				/>
			),
		},
		{
			value: 'overdue',
			label: (
				<StatusBtn
					label="Overdue"
					variant="closed"
					className={styles.statusOption}
				/>
			),
		},
	]

	const staffForLookup = useMemo(() => {
		const fromStaff = (staff || [])
			.map((s) => ({
				_id: String(s.staffId || s._id || ''),
				name: s.name?.trim() || s.email?.trim() || 'Unknown',
				avatar: s.avatar || undefined,
			}))
			.filter((u) => u._id)
		const me =
			authUser?._id
				? [
						{
							_id: String(authUser._id),
							name: (
								authUser.businessName ||
								authUser.name ||
								authUser.email ||
								'Me'
							).trim(),
							avatar:
								authUser.avatar ||
								authUser.profilePic ||
								undefined,
						},
					]
				: []
		const seen = new Set()
		return [...me, ...fromStaff].filter(
			(u) => !seen.has(u._id) && seen.add(u._id)
		)
	}, [staff, authUser])

	const handleStatusChange = (v, rowId) => {
		const statusKey = getVal(v)

		if (!rowId) {
			setStatusVal(statusKey)
			return
		}
		const task = tasksOnly.find((t) => String(t._id) === String(rowId))
		if (!task) return

		const statusLabel = STATUS_LABEL[statusKey] || 'New'

		const payload = {
			status: statusLabel,
		}

		dispatch(updateVendorTask({ id: rowId, data: payload }))
	}

	const columns = useMemo(
		() => [
			{
				field: 'task',
				headerName: 'Task',
				flex: 1,
				minWidth: 220,
				sortable: true,
				renderCell: (params) => (
					<span className="role-text">{params.value || '-'}</span>
				),
			},
			{
				field: 'assignedTo',
				headerName: 'Assigned To',
				flex: 1.6,
				minWidth: 160,
				sortable: true,
				renderCell: (params) => {
					const { avatar, assignedTo } = params.row
					return (
						<Stack
							direction="row"
							spacing={1.5}
							alignItems="center"
							sx={{ height: '100%' }}
							className="img-with-text-wrp"
						>
							<Avatar
								src={
									avatar ||
									'https://static.vecteezy.com/system/resources/previews/020/911/740/non_2x/user-profile-icon-profile-avatar-user-icon-male-icon-face-icon-profile-icon-free-png.png'
								}
								sx={{
									width: 40,
									height: 40,
									borderRadius: '50%',
									'@media (max-width: 767px)': {
										width: 30,
										height: 30,
									},
								}}
							/>
							<Box
								component="span"
								sx={{ fontWeight: 600 }}
								className="img-with-text"
							>
								{assignedTo}
							</Box>
						</Stack>
					)
				},
			},
			{
				field: 'dueDate',
				headerName: 'Due Date',
				flex: 1,
				minWidth: 140,
				sortable: true,
				renderCell: (params) => (
					<span className="role-text">{params.value || '-'}</span>
				),
			},
			{
				field: 'priority',
				headerName: 'Priority',
				flex: 1,
				minWidth: 100,
				renderCell: (params) => {
					const { priority } = params.row
					return (
						<Stack
							direction="row"
							spacing={1.5}
							alignItems="center"
						>
							<Box
								component="span"
								sx={{ fontWeight: 600 }}
								className="priority-icon"
							>
								<img
									src={
										priority === 'High'
											? '/images/high-prio-icon.svg'
											: priority === 'Medium'
												? '/images/med-prio-icon.svg'
												: '/images/low-prio-icon.svg'
									}
									alt="priority.png"
								/>
								{priority}
							</Box>
						</Stack>
					)
				},
			},
			{
				field: 'comments',
				headerName: 'Comments',
				flex: 1,
				minWidth: 200,
				sortable: false,
				renderCell: (params) => (
					<span className="role-text">{params.value || '-'}</span>
				),
			},
			{
				field: 'status',
				headerName: 'Status',
				flex: 1,
				minWidth: 130,
				renderCell: (params) => {
					const status = params.value
					const rowId = params.row.id

					return (
						<SelectCustom
							disabled={restrictedAccountTypes.includes(
								accountType
							)}
							options={status_options}
							defaultValue={status.toLowerCase().replace(' ', '')}
							setDataFunction={(newStatus) =>
								handleStatusChange(newStatus, rowId)
							}
							renderValueAsLabel={true}
						/>
					)
				},
			},
			// {
			// 	field: 'menu',
			// 	headerName: '',
			// 	sortable: false,
			// 	filterable: false,
			// 	width: 64,
			// 	align: 'right',
			// 	renderCell: (params) => (
			// 		<Box
			// 			sx={{
			// 				display: 'flex',
			// 				alignItems: 'center',
			// 				justifyContent: 'flex-end',
			// 				width: '100%',
			// 				height: '100%',
			// 			}}
			// 		>
			// 			<IconButton
			// 				data-cy="kebab"
			// 				size="small"
			// 				onClick={(e) => openMenu(e, params.row)}
			// 				sx={{ p: 0.5 }}
			// 			>
			// 				<img src="/images/more.svg" alt="" />
			// 			</IconButton>
			// 		</Box>
			// 	),
			// },
		],
		[status_options, handleStatusChange]
	)

	const filteredRows = useMemo(() => {
		const q = search.trim().toLowerCase()
		return rows.filter((r) => {
			if (q && !r.task.toLowerCase().includes(q)) return false
			if (statusFilter.length && !statusFilter.includes(r.status))
				return false
			if (priorityFilter.length && !priorityFilter.includes(r.priority))
				return false
			return true
		})
	}, [rows, search, statusFilter, priorityFilter])

	const chips = [
		...statusFilter.map((s) => ({
			key: `status-${s}`,
			label: `Status ${s}`,
			onDelete: () =>
				setStatusFilter((prev) => prev.filter((x) => x !== s)),
		})),
		...priorityFilter.map((r) => ({
			key: `priority-${r}`,
			label: `Priority ${r}`,
			onDelete: () =>
				setPriorityFilter((prev) => prev.filter((x) => x !== r)),
		})),
	]

	const clearFilters = () => {
		setStatusFilter(availableStatuses)
		setPriorityFilter([])
	}

	const openFilter = (e) => setFilterAnchor(e.currentTarget)
	const closeFilter = () => setFilterAnchor(null)

	const exportCSV = () => {
		const header = [
			'Task',
			'Created From',
			'Assigned To',
			'Due Date',
			'Priority',
			'Comments',
			'Status',
		]
		const rowsCSV = filteredRows.map((r) => [
			r.task,
			r.source,
			r.assignedTo,
			r.dueDate,
			r.priority,
			r.comments,
			r.status,
		])
		const csv = [header, ...rowsCSV]
			.map((r) => r.map(esc).join(','))
			.join('\n')
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
		const url = URL.createObjectURL(blob)
		const a = document.createElement('a')
		a.href = url
		a.download = 'vendor-tasks.csv'
		a.click()
		URL.revokeObjectURL(url)
	}

	const openMenu = (e, row) => {
		e.stopPropagation()
		setMenuAnchor(e.currentTarget)
		setMenuRow(row)
		setSelectedTaskId(row?.id || null)
	}
	const closeMenu = () => {
		setMenuAnchor(null)
		setMenuRow(null)
	}

	const handleSelect = (member) => {
		setSelectedMembers((prev) => [...prev, member])
	}

	const handleRemove = (memberId) => {
		setSelectedMembers((prev) =>
			prev.filter((m) => String(m._id) !== String(memberId))
		)
	}

	const handleSubmit = async (e) => {
		e.preventDefault()
		if (!taskTitle.trim()) {
			toastHelper('Task title is required', 'error')
			return
		}
		if (!eventCategory.value.length) {
			toastHelper('Please select an event category', 'error')
			return
		}
		if (!dueDate) {
			toastHelper('Please choose a due date', 'error')
			return
		}

		const due =
			dueDate instanceof Date
				? dueDate.toISOString()
				: new Date(dueDate).toString() !== 'Invalid Date'
					? new Date(dueDate).toISOString()
					: undefined

		if (!due) {
			toastHelper('Invalid due date', 'error')
			return
		}

		const priorityKey = getVal(priorityVal)
		const statusKey = getVal(statusVal)
		const finalAssigneeIds = getNormalizedAssigneeIds(selectedMembers)

		if (!finalAssigneeIds.length) {
			toastHelper(
				'Please assign at least one member (yourself or staff)',
				'error'
			)
			return
		}

		const data = {
			title: taskTitle.trim(),
			selectedMembers: finalAssigneeIds,
			createdFrom: 'tasks-page',
			StartTime: due,
			EndTime: due,
			priority: PRIORITY_LABEL[priorityKey] ?? 0,
			status: STATUS_LABEL[statusKey] || 'New',
			comments: text,
			eventCategory: eventCategory?.value || '',
			...(selectedWedding && selectedWedding !== 'none'
				? {
						kazooDetails: {
							kazooId: selectedWedding,
							kazooName: weddingById?.kazooName || weddingById?.name,
						},
					}
				: {}),
			syncToCalendar,
		}

		await dispatch(createVendorTask({ data })).unwrap()
		await dispatch(getVendorEvents('isVendorTasks')).unwrap()
		clearTaskModal()
		closeAllModals()
	}

	const onConfirmEdit = async () => {
		if (!selectedTaskId) {
			toastHelper('No task selected', 'error')
			return
		}
		if (!taskTitle.trim()) {
			toastHelper('Task title is required', 'error')
			return
		}
		if (!eventCategory.value.length) {
			toastHelper('Please select an event category', 'error')
			return
		}
		if (!dueDate) {
			toastHelper('Please choose a due date', 'error')
			return
		}

		const due =
			dueDate instanceof Date
				? dueDate.toISOString()
				: new Date(dueDate).toString() !== 'Invalid Date'
					? new Date(dueDate).toISOString()
					: undefined

		if (!due) {
			toastHelper('Invalid due date', 'error')
			return
		}

		const priorityKey = getVal(priorityVal)
		const statusKey = getVal(statusVal)
		const finalAssigneeIds = getNormalizedAssigneeIds(selectedMembers)

		if (!finalAssigneeIds.length) {
			toastHelper(
				'Please assign at least one member (yourself or staff)',
				'error'
			)
			return
		}

		const data = {
			title: taskTitle.trim(),
			selectedMembers: finalAssigneeIds,
			createdFrom: 'tasks-page',
			StartTime: due,
			EndTime: due,
			priority: PRIORITY_LABEL[priorityKey] ?? 0,
			status: STATUS_LABEL[statusKey] || 'New',
			comments: text,
			eventCategory: eventCategory?.value || '',
			kazooDetails:
				selectedWedding && selectedWedding !== 'none'
					? {
							kazooId: selectedWedding,
							kazooName: weddingById?.kazooName || weddingById?.name,
						}
					: 'none',
			syncToCalendar,
		}

		await dispatch(updateVendorTask({ id: selectedTaskId, data })).unwrap()
		await dispatch(getVendorEvents('isVendorTasks')).unwrap()
		clearTaskModal()
		closeAllModals()
		
	}

	const currentTask = useMemo(
		() => tasksOnly.find((t) => String(t._id) === String(selectedTaskId)),
		[tasksOnly, selectedTaskId]
	)

	const currentTasksMap = useMemo(() => {
		const m = new Map()
		for (const t of tasksOnly) m.set(String(t._id), t)
		return m
	}, [tasksOnly])

	const assignedMembersForTask = useMemo(() => {
		const ids = new Set((currentTask?.OwnerIds || []).map(String))
		return staffForLookup.filter((u) => ids.has(String(u._id)))
	}, [currentTask, staffForLookup])

	const dueDisplay = useMemo(() => {
		const d = currentTask?.EndTime || currentTask?.StartTime
		if (!d) return '-'
		return formatUtcToLocalDate(d)
	}, [currentTask])

	const prLabel = useMemo(() => {
		const p = Number(currentTask?.priority)
		return p === 0
			? 'High'
			: p === 1
				? 'Medium'
				: p === 2
					? 'Low'
					: 'Unknown'
	}, [currentTask])

	const prIcon =
		prLabel === 'High'
			? '/images/high-prio-icon.svg'
			: prLabel === 'Medium'
				? '/images/med-prio-icon.svg'
				: '/images/low-prio-icon.svg'

	const statusText = currentTask?.status || 'New'
	const statusVariant =
		statusText === 'Completed'
			? 'submitted'
			: statusText === 'Overdue'
				? 'closed'
				: statusText === 'In Progress' || statusText === 'In progress'
					? 'pending'
					: 'active'

	const openCreateModal = () => setActiveModal('create')
	const openDeleteModal = () => setActiveModal('delete')
	const closeAllModals = () => setActiveModal('')

	const handleInnerTab = (e, tab) => {
		setInnerTab(tab)
	}

	const handleRowClick = ({ row }) => {
		setSelectedTaskId(row?.id || null)
		setActiveModal('detail')
	}

	const onConfirmDelete = async () => {
		if (!selectedTaskId) {
			toastHelper('No task selected', 'error')
			return
		}
		const payload = await dispatch(deleteVendorTask({ id: selectedTaskId })).then((res) => res.payload)
		if (payload?.success) {
			toastHelper('Task deleted', 'success')
			setActiveModal('')
			setSelectedTaskId(null)
			dispatch(getVendorEvents('isVendorTasks'))
		} else toastHelper(payload?.message || 'Failed to delete task', 'error')
	}

	const clearTaskModal = () => {
		setTaskTitle('')
		setSelectedMembers([])
		setDueDate(null)
		setPriorityVal('high')
		setStatusVal('new')
		setEventCategory({ value: '', label: '' })
		setText('')
		setSelectedWedding('none')
		setSyncToCalendar({ google: true, outlook: true, ical: true })
	}

	return (
		<div className={styles.pageWrap}>
			<div className={styles.pageHeader}>
				<Typography component="h1">Tasks</Typography>
				{!restrictedAccountTypes.includes(accountType) && (
					<div className={styles.right}>
						<ButtonCustom
							variant="default"
							size="lg"
							onClick={() => {
								clearTaskModal()
								openCreateModal()
							}}
							data-cy="add-task"
						>
							+ Add Task
						</ButtonCustom>
						<SyncAllModal />
					</div>
				)}
			</div>

			<Box className={styles.tableWrap}>
				<ListToolbar
					title="Task"
					searchPlaceholder="Search task"
					searchValue={search}
					onSearchChange={setSearch}
					chips={chips}
					showTabes={true}
					innerTab={innerTab}
					onInnerTabChange={handleInnerTab}
					onClearFilters={clearFilters}
					onOpenFilter={openFilter}
					onExport={exportCSV}
				/>
				<FilterPopover
					anchorEl={filterAnchor}
					open={Boolean(filterAnchor)}
					onClose={closeFilter}
					onClear={clearFilters}
					onApply={closeFilter}
					sections={[
						{
							id: 'status',
							title: 'Status',
							options: [
								'New',
								'Completed',
								'In Progress',
								'Overdue',
							],
							selected: statusFilter,
							onToggle: (val, checked) =>
								setStatusFilter((prev) =>
									checked
										? [...prev, val]
										: prev.filter((x) => x !== val)
								),
						},
						{
							id: 'priority',
							title: 'Priority',
							options: ['High', 'Medium', 'Low'],
							selected: priorityFilter,
							onToggle: (val, checked) =>
								setPriorityFilter((prev) =>
									checked
										? [...prev, val]
										: prev.filter((x) => x !== val)
								),
						},
					]}
				/>

				{innerTab === 0 ? (
					<DataGridTable
						rows={filteredRows}
						columns={columns}
						onRowClick={handleRowClick}
					/>
				) : (
					<div className="calendarWrp vendorTask">
						<div className="calendarInner">
							<VendorTimeline height="77dvh" />
						</div>
					</div>
				)}

				{/* <KebabMenu
					anchorEl={menuAnchor}
					open={Boolean(menuAnchor)}
					onClose={closeMenu}
					items={[
						{
							key: 'edit',
							label: 'Edit',
							icon: (
								<>
									<img src="/images/edit-icon.svg" alt="" />
								</>
							),
							onClick: () => {
								if (!menuRow?.id) return
								const t = currentTasksMap.get(
									String(menuRow.id)
								)
								if (!t) return

								setSelectedTaskId(menuRow.id)

								setTaskTitle(t.Subject || '')

								setText(t.Description || '')

								setDueDate(t.EndTime || t.StartTime || null)

								setPriorityVal(
									Number(t.priority) === 0
										? 'high'
										: Number(t.priority) === 1
											? 'medium'
											: 'low'
								)

								const s = (t.status || 'New').toLowerCase()
								setStatusVal(
									s.startsWith('completed')
										? 'completed'
										: s.startsWith('overdue')
											? 'overdue'
											: s.startsWith('in progress')
												? 'inprogress'
												: 'new'
								)

								const ids = new Set(
									(t.OwnerIds || []).map(String)
								)
								setSelectedMembers(
									staffForLookup.filter((u) =>
										ids.has(String(u._id))
									)
								)

								setEventCategory(
									t.EventCategory
										? {
												value: t.EventCategory,
												label: t.EventCategory,
											}
										: { value: '', label: '' }
								)

								setActiveModal('edit')
							},
						},
						{
							key: 'delete',
							label: 'Delete',
							icon: (
								<>
									<img src="/images/trash.svg" alt="" />
								</>
							),
							onClick: () => {
								setSelectedTaskId(menuRow?.id || null)
								openDeleteModal()
							},

							color: 'error.main',
						},
					]}
				/> */}
			</Box>

			{/* Create Modal */}
			<CommonModal
				open={activeModal === 'create'}
				handleClose={() => {
					clearTaskModal()
					setActiveModal('')
				}}
				heading="Add Task"
				footerContent={
					<>
						<ButtonCustom
							onClick={() => {
								clearTaskModal()
								setActiveModal('')
							}}
							// disabled={loading}
							variant="outlined"
							size="lg"
						>
							Cancel
						</ButtonCustom>
						<ButtonCustom
							variant="default"
							size="lg"
							onClick={handleSubmit}
							// disabled={!canSubmit}
						>
							Add Task
						</ButtonCustom>
					</>
				}
			>
				<form onSubmit={handleSubmit} className={styles.formWrp}>
					<Grid container spacing={1.5}>
						<Grid size={{ xs: 12, md: 12 }}>
							<div className={styles.formField}>
								<label htmlFor="task" className={styles.label}>
									Task Title
								</label>
								<input
									type="text"
									name="task"
									id="task"
									placeholder="Enter task title*"
									className={styles.taskTitleInput}
									value={taskTitle}
									onChange={(e) => setTaskTitle(e.target.value)}
								/>
							</div>
						</Grid>
						<Grid size={{ xs: 12, md: 12 }}>
							<div className={styles.formField}>
								<label htmlFor="name" className={styles.label}>
									Assigned to
								</label>
								<MultiselectSearch
									members={availableMembers}
									selected={selectedMembers}
									onSelect={handleSelect}
									onRemove={handleRemove}
									// label="Assigned to"
									placeholder="Select"
									searchPlaceholder="Search user..."
								/>
							</div>
						</Grid>
						<Grid size={{ xs: 12, md: 12 }}>
							<div className={styles.formField}>
								<label
									htmlFor="selectWedding"
									className={styles.label}
								>
									Select Wedding
								</label>
								<SelectCustom
									options={weddings}
									label={
										reduxWeddings?.find(
											(wedding) =>
												wedding._id === selectedWedding
										)?.name || 'Select Wedding'
									}
									setDataFunction={({ value }) => {
										setSelectedWedding(value)
									}}
									renderValueAsLabel={true}
								/>
							</div>
						</Grid>
						<Grid size={{ xs: 12, md: 12 }}>
							<div className={styles.formField}>
								<label
									htmlFor="EventCategory"
									className={styles.label}
								>
									Event Category*
								</label>
								<SelectCustom
									label="Event category"
									options={eventCategoriesOptions}
									defaultValue={eventCategory.value}
									setDataFunction={setEventCategory}
									renderValueAsLabel={true}
								/>
							</div>
						</Grid>
						<Grid size={{ xs: 12, md: 12 }}>
							<div className={styles.formField}>
								<label
									htmlFor="DueDate"
									className={styles.label}
								>
									Due Date*
								</label>
								<DateCustom
									value={dueDate}
									onChange={setDueDate}
								/>
							</div>
						</Grid>
						<Grid size={{ xs: 12, sm: 6 }}>
							<div className={styles.formField}>
								<label
									htmlFor="Priority"
									className={styles.label}
								>
									Priority
								</label>
								<SelectCustom
									options={priority_options}
									value={priorityVal}
									setDataFunction={setPriorityVal}
									label={
										priority_options.find(
											(o) => o.value === priorityVal
										)?.label
									}
									renderValueAsLabel={true}
								/>
							</div>
						</Grid>
						<Grid size={{ xs: 12, sm: 6 }}>
							<div className={styles.formField}>
								<label
									htmlFor="Status"
									className={styles.label}
								>
									Status
								</label>
								<SelectCustom
									options={status_options}
									value={statusVal}
									setDataFunction={setStatusVal}
									label={
										status_options.find(
											(o) => o.value === statusVal
										)?.label
									}
									renderValueAsLabel={true}
								/>
							</div>
						</Grid>
						<Grid size={{ xs: 12, md: 12 }}>
							<div className={styles.formField}>
								<label
									htmlFor="Status"
									className={styles.label}
								>
									Comments
								</label>
								<TextArea
									placeholder="Add comments"
									value={text}
									onChange={(e) => setText(e.target.value)}
								/>
							</div>
						</Grid>
						<Grid size={{ xs: 12, md: 12 }}>
							<div className={styles.formField}>
								<CalendarSync
									value={syncToCalendar}
									onChange={setSyncToCalendar}
									labelClassName={styles.label}
								/>
							</div>
						</Grid>
						{/* <Grid size={{ xs: 12, md: 12 }}>
							<div className={styles.formField}>
								<label
									htmlFor="Attachment"
									className={styles.label}
								>
									Attachment
								</label>
								<FileAttach
									label="Choose File"
									placeholder="No Files Selected"
									accept=".jpg,.png,.pdf"
									multiple
									onChange={(e) =>
										console.log(e.target.files)
									}
								/>
							</div>
						</Grid>
						<Grid size={{ xs: 12, sm: 6 }}>
							<div className={styles.formField}>
								<ButtonCustom
									variant="outlined"
									size="lg"
									className={styles.fileUploadedList}
								>
									<img src="/images/document.svg" />
									<Typography variant="body">
										Task Details.pdf
									</Typography>
									<img src="/images/file-close-icon-black.svg" />
								</ButtonCustom>
							</div>
						</Grid>
						<Grid size={{ xs: 12, sm: 6 }}>
							<div className={styles.formField}>
								<ButtonCustom
									variant="outlined"
									size="lg"
									className={styles.fileUploadedList}
								>
									<img src="/images/document.svg" />
									<Typography variant="body">
										Task Details.pdf
									</Typography>
									<img src="/images/file-close-icon-black.svg" />
								</ButtonCustom>
							</div>
						</Grid> */}
					</Grid>
				</form>
			</CommonModal>

			{/* Edit Modal */}
			<CommonModal
				open={activeModal === 'edit'}
				handleClose={closeAllModals}
				heading="Edit Task"
				className={styles.taskEditModal}
				footerContent={
					<>
						<ButtonCustom
							onClick={closeAllModals}
							// disabled={loading}
							variant="outlined"
							size="lg"
						>
							Cancel
						</ButtonCustom>
						<ButtonCustom
							variant="default"
							size="lg"
							onClick={onConfirmEdit}
						>
							Update
						</ButtonCustom>
					</>
				}
			>
				<>
					<Typography
						variant="body1"
						className={styles.subHeading}
					></Typography>
					<form onSubmit={handleSubmit} className={styles.formWrp}>
						<Grid container spacing={1.5}>
							<Grid size={{ xs: 12, md: 12 }}>
								<div className={styles.formField}>
									<label htmlFor="task" className={styles.label}>
										Task Title
									</label>
									<input
										type="text"
										name="task"
										id="task"
										placeholder="Enter task title*"
										className={styles.taskTitleInput}
										value={taskTitle}
										onChange={(e) =>
											setTaskTitle(e.target.value)
										}
									/>
								</div>
							</Grid>
							<Grid size={{ xs: 12, md: 12 }}>
								<div className={styles.formField}>
									<label
										htmlFor="name"
										className={styles.label}
									>
										Assigned to
									</label>
									<MultiselectSearch
										members={availableMembers}
										selected={selectedMembers}
										onSelect={handleSelect}
										onRemove={handleRemove}
										// label="Assigned to"
										placeholder="Select"
										searchPlaceholder="Search user..."
									/>
								</div>
							</Grid>
							<Grid size={{ xs: 12, md: 12 }}>
								<div className={styles.formField}>
									<label
										htmlFor="selectWedding"
										className={styles.label}
									>
										Select Wedding
									</label>
									<SelectCustom
										options={weddings}
										label={weddingById?.name || 'None'}
										setDataFunction={({ value }) => {
											setSelectedWedding(value)
										}}
										defaultValue={selectedWedding}
										renderValueAsLabel={true}
									/>
								</div>
							</Grid>
							<Grid size={{ xs: 12, md: 12 }}>
								<div className={styles.formField}>
									<label
										htmlFor="EventCategory"
										className={styles.label}
									>
										Event Category*
									</label>
									<SelectCustom
										label="Event category"
										options={eventCategoriesOptions}
										defaultValue={eventCategory.value}
										setDataFunction={setEventCategory}
										renderValueAsLabel={true}
									/>
								</div>
							</Grid>
							<Grid size={{ xs: 12, md: 12 }}>
								<div className={styles.formField}>
									<label
										htmlFor="DueDate"
										className={styles.label}
									>
										Due Date*
									</label>
									<DateCustom
										value={dueDate}
										onChange={setDueDate}
									/>
								</div>
							</Grid>
							<Grid size={{ xs: 12, sm: 6 }}>
								<div className={styles.formField}>
									<label
										htmlFor="Priority"
										className={styles.label}
									>
										Priority
									</label>
									<SelectCustom
										options={priority_options}
										setDataFunction={setPriorityVal}
										label={
											priority_options.find(
												(o) => o.value === priorityVal
											)?.label
										}
										renderValueAsLabel={true}
									/>
								</div>
							</Grid>
							<Grid size={{ xs: 12, sm: 6 }}>
								<div className={styles.formField}>
									<label
										htmlFor="Status"
										className={styles.label}
									>
										Status
									</label>
									<SelectCustom
										options={status_options}
										setDataFunction={setStatusVal}
										label={
											status_options.find(
												(o) => o.value === statusVal
											)?.label
										}
										renderValueAsLabel={true}
									/>
								</div>
							</Grid>
							<Grid size={{ xs: 12, md: 12 }}>
								<div className={styles.formField}>
									<label
										htmlFor="Status"
										className={styles.label}
									>
										Comments
									</label>
									<TextArea
										placeholder="Add comments"
										value={text}
										onChange={(e) =>
											setText(e.target.value)
										}
									/>
								</div>
							</Grid>
							<Grid size={{ xs: 12, md: 12 }}>
								<div className={styles.formField}>
									<CalendarSync
										value={syncToCalendar}
										onChange={setSyncToCalendar}
										labelClassName={styles.label}
									/>
								</div>
							</Grid>
							{/* <Grid size={{ xs: 12, md: 12 }}>
								<div className={styles.formField}>
									<label
										htmlFor="Attachment"
										className={styles.label}
									>
										Attachment
									</label>
									<FileAttach
										label="Choose File"
										placeholder="No Files Selected"
										accept=".jpg,.png,.pdf"
										multiple
										onChange={(e) =>
											console.log(e.target.files)
										}
									/>
								</div>
							</Grid>
							<Grid size={{ xs: 12, sm: 6 }}>
								<div className={styles.formField}>
									<ButtonCustom
										variant="outlined"
										size="lg"
										className={styles.fileUploadedList}
									>
										<img src="/images/document.svg" />
										<Typography variant="body">
											Task Details.pdf
										</Typography>
										<img src="/images/file-close-icon-black.svg" />
									</ButtonCustom>
								</div>
							</Grid>
							<Grid size={{ xs: 12, sm: 6 }}>
								<div className={styles.formField}>
									<ButtonCustom
										variant="outlined"
										size="lg"
										className={styles.fileUploadedList}
									>
										<img src="/images/document.svg" />
										<Typography variant="body">
											Task Details.pdf
										</Typography>
										<img src="/images/file-close-icon-black.svg" />
									</ButtonCustom>
								</div>
							</Grid> */}
						</Grid>
					</form>
				</>
			</CommonModal>

			{/* Detail Modal */}
			<CommonModal
				open={activeModal === 'detail'}
				handleClose={closeAllModals}
				heading="Task Detail"
				className={styles.taskDetailsModal}
				footerContent={
					!restrictedAccountTypes.includes(accountType) && (
						<>
							<ButtonCustom
								onClick={openDeleteModal}
								variant="outlined"
								size="lg"
							>
								Delete
							</ButtonCustom>
							<ButtonCustom
								variant="default"
								size="lg"
								onClick={() => {
									const t = currentTask
									setTaskTitle(t?.Subject || '')
									const d = t?.EndTime || t?.StartTime
									const localDate = d
										? formatUtcToLocalDate(d)
										: null
									setDueDate(localDate)
									setPriorityVal(
										Number(t?.priority) === 0
											? 'high'
											: Number(t?.priority) === 1
												? 'medium'
												: 'low'
									)
									const s = (t?.status || 'New').toLowerCase()
									setStatusVal(
										s.startsWith('overdue')
											? 'overdue'
											: s.startsWith('completed')
												? 'completed'
												: s.startsWith('in progress')
													? 'inprogress'
													: 'new'
									)

									const ids = new Set(
										(t?.OwnerIds || []).map(String)
									)
									setSelectedMembers(
										staffForLookup.filter((u) =>
											ids.has(String(u._id))
										)
									)

									setEventCategory(
										t?.EventCategory
											? {
													value: t.EventCategory,
													label: t.EventCategory,
												}
											: { value: '', label: '' }
									)
									const sync = t?.syncToCalendar
									setSyncToCalendar({
										google: sync?.google !== false,
										outlook: sync?.outlook !== false,
										ical: sync?.ical !== false,
									})

									setText(t?.Description || '')

									setSelectedWedding(
										t?.KazooDetails?.kazooId || 'none'
									)

									setActiveModal('edit')
								}}
							>
								Edit
							</ButtonCustom>
						</>
					)
				}
			>
				<>
					<Typography variant="body1" className={styles.subHeading}>
						{currentTask?.Subject || 'Untitled Task'}
					</Typography>
					<form onSubmit={handleSubmit} className={styles.formWrp}>
						<Grid container spacing={1.7}>
							<Grid size={{ xs: 12, md: 12 }}>
								<div className={styles.formField}>
									<label
										htmlFor="name"
										className={styles.label}
									>
										Assigned to
									</label>
									<ul className={styles.assignedToRow}>
										{assignedMembersForTask.length ? (
											assignedMembersForTask.map((m) => (
												<li key={m._id} title={m.name}>
													<img
														src={
															m.avatar ||
															'https://static.vecteezy.com/system/resources/previews/020/911/740/non_2x/user-profile-icon-profile-avatar-user-icon-male-icon-face-icon-profile-icon-free-png.png'
														}
														alt={m.name}
													/>
												</li>
											))
										) : (
											<li>
												<Typography variant="body2">
													N/A
												</Typography>
											</li>
										)}
									</ul>
								</div>
							</Grid>
							<Grid size={{ xs: 12, md: 12 }}>
								<div className={styles.formField}>
									<label
										htmlFor="EventCategory"
										className={styles.label}
									>
										Select Wedding
									</label>
									<div className={styles.dueDate}>
										{currentTask?.KazooDetails?.kazooName || '-'}
									</div>
								</div>
							</Grid>
							<Grid size={{ xs: 12, md: 12 }}>
								<div className={styles.formField}>
									<label className={styles.label}>
										Event Category
									</label>
									<div className={styles.dueDate}>
										{currentTask?.EventCategory || '-'}
									</div>
								</div>
							</Grid>
							<Grid size={{ xs: 12, md: 12 }}>
								<div className={styles.formField}>
									<label className={styles.label}>
										Due Date
									</label>
									<div className={styles.dueDate}>
										{dueDisplay}
									</div>
								</div>
							</Grid>
							<Grid size={{ xs: 12, sm: 12 }}>
								<div className={styles.formField}>
									<label className={styles.label}>
										Priority
									</label>
									<div
										className={`${styles.prioritySelect} prioritySelect`}
									>
										<img src={prIcon} alt="" />
										{prLabel}
									</div>
								</div>
							</Grid>
							<Grid size={{ xs: 12, sm: 12 }}>
								<div className={styles.formField}>
									<label className={styles.label}>
										Status
									</label>
									<StatusBtn
										label={statusText}
										variant={statusVariant}
										className={styles.status}
									/>
								</div>
							</Grid>
							<Grid size={{ xs: 12, md: 12 }}>
								<div className={styles.formField}>
									<label className={styles.label}>
										Comments
									</label>
									<div className={styles.comment}>
										{currentTask?.Description || '-'}
									</div>
								</div>
							</Grid>

							{/* <Grid size={{ xs: 12, sm: 12 }}>
								<div className={styles.formField}>
									<label
										htmlFor="Attachment"
										className={styles.label}
									>
										Attachment
									</label>
									<Grid container spacing={1.5}>
										<Grid size={{ xs: 12, sm: 6 }}>
											<ButtonCustom
												variant="outlined"
												size="lg"
												className={
													styles.fileUploadedList
												}
											>
												<img src="/images/document.svg" />
												<Typography variant="body">
													Task Details.pdf
												</Typography>
												<img src="/images/task-detail-download.svg" />
											</ButtonCustom>
										</Grid>
										<Grid size={{ xs: 12, sm: 6 }}>
											<ButtonCustom
												variant="outlined"
												size="lg"
												className={
													styles.fileUploadedList
												}
											>
												<img src="/images/document.svg" />
												<Typography variant="body">
													Task Details.pdf
												</Typography>
												<img src="/images/task-detail-download.svg" />
											</ButtonCustom>
										</Grid>
									</Grid>
								</div>
							</Grid> */}
						</Grid>
					</form>
				</>
			</CommonModal>

			{/* Delete modal */}
			<CommonDeleteModal
				open={activeModal === 'delete'}
				handleClose={closeAllModals}
				heading="Delete Task?"
				subHeading="Are you sure you want to delete this task?"
				modalBody={styles.modalBody}
				className={styles.deleteModal}
			>
				<form className={styles.formBox}>
					<div
						className={styles.modalBtns}
						style={{ justifyContent: 'center', marginTop: '0' }}
					>
						<ButtonCustom
							variant="outlined"
							type="button"
							size="lg"
							onClick={closeAllModals}
						>
							Cancel
						</ButtonCustom>
						<ButtonCustom
							variant="destructive"
							size="lg"
							onClick={onConfirmDelete}
						>
							Delete
						</ButtonCustom>
					</div>
				</form>
			</CommonDeleteModal>
		</div>
	)
}
